// src/services/locationWebSocket.js
/**
 * WebSocket service to send driver location to:
 * wss://aeroviewbackend.dockyardsoftware.com:8095/location
 * 
 * When driver goes online, this continuously sends location updates
 * Employee dashboard listens to the same WS and displays in real time
 */

class LocationWebSocket {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 2000;
    this.sendLocationInterval = null;
    this.reconnectTimeout = null; // Track pending reconnect
    this.driverData = null;
    this.authKey = null;
    this.WS_URL = "wss://aeroviewbackend.dockyardsoftware.com:8095/location";
    this.manuallyDisconnected = false; // Flag to prevent auto-reconnect after manual disconnect
    this.lastSentData = null; // Track last sent data to prevent duplicates
    this.lastSentTime = 0; // Track time of last send
    this.minSendInterval = 5000; // Min 5s between sends to prevent spam
  }

  /**
   * Connect to location WS server
   * @param {string} authKey - authentication token
   * @param {object} driverData - { vehicleId, driverSno, driverName, vehicle_type, latitude, longitude, status, bStatus }
   */
  connect(authKey, driverData) {
    if (!authKey || !driverData || !driverData.vehicleId) {
      console.warn("LocationWS: Missing authKey or driverData");
      return;
    }

    this.authKey = authKey;
    this.driverData = driverData;
    this.manuallyDisconnected = false; // Reset flag when connecting - allow reconnects
    this.reconnectAttempts = 0; // Reset reconnect counter

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      console.log("LocationWS: Already connected or connecting");
      return;
    }

    // Clean up only the interval and timeout, NOT the disconnect flag
    if (this.sendLocationInterval) {
      clearInterval(this.sendLocationInterval);
      this.sendLocationInterval = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Close old connection without setting manuallyDisconnected
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {}
      this.ws = null;
    }

    try {
      // Try with token as query param
      const url = this.WS_URL + (this.WS_URL.includes("?") ? "&" : "?") + "token=" + encodeURIComponent(authKey);
      console.log("[LocationWS] Connecting to:", this.WS_URL);
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        console.log("[LocationWS] Connected! Ready to send location updates");

        // Send initial auth message
        try {
          this.ws.send(JSON.stringify({
            type: "auth",
            token: authKey,
            action: "authenticate"
          }));
        } catch (e) {
          console.error("[LocationWS] Auth send failed:", e);
        }

        // Send initial location
        this.sendLocationUpdate();

        // Then send every 15 seconds while connected
        if (this.sendLocationInterval) clearInterval(this.sendLocationInterval);
        this.sendLocationInterval = setInterval(() => {
          if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.sendLocationUpdate();
          }
        }, 15000); // 15 seconds
      };

      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          console.log("[LocationWS] Server message:", msg);
        } catch (e) {
          console.log("[LocationWS] Received (not JSON):", evt.data);
        }
      };

      this.ws.onerror = (err) => {
        console.error("[LocationWS] Error:", err);
      };

      this.ws.onclose = () => {
        this.isConnected = false;
        if (this.sendLocationInterval) clearInterval(this.sendLocationInterval);
        console.log("[LocationWS] Closed");
        // Only attempt reconnect if NOT manually disconnected
        if (!this.manuallyDisconnected) {
          this.attemptReconnect();
        } else {
          console.log("[LocationWS] Manual disconnect - NOT reconnecting");
        }
      };
    } catch (err) {
      console.error("[LocationWS] Failed to create WebSocket:", err);
      this.attemptReconnect();
    }
  }

  /**
   * Send current location update to server (with deduplication)
   */
  sendLocationUpdate() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.driverData) {
      return;
    }

    // Prevent sending duplicate updates too frequently (min 5 seconds between sends)
    const now = Date.now();
    if (now - this.lastSentTime < this.minSendInterval) {
      console.log("[LocationWS] Skipping update - sent too recently (<5s)");
      return;
    }

    // Only send notification with vehicleId
    const payload = {
      type: "location_update",
      vehicleId: this.driverData.vehicleId,
      driverSno: this.driverData.driverSno, // Critical for admin to identify user
      latitude: this.driverData.latitude,
      longitude: this.driverData.longitude,
      status: this.driverData.status,
      oStatus: this.driverData.oStatus,
      timestamp: new Date().toISOString()
    };

    try {
      this.ws.send(JSON.stringify(payload));
      this.lastSentTime = now;
      console.log("[LocationWS] Sent location notification:", payload.vehicleId);
    } catch (err) {
      console.error("[LocationWS] Failed to send location notification:", err);
    }
  }

  /**
   * Send offline signal before disconnect
   */
  async sendOfflineSignal() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.driverData) {
      console.log("[LocationWS] Cannot send offline signal - WS not ready or no driver data");
      return;
    }

    const payload = {
      type: "status_update",
      action: "offline",
      vehicleId: this.driverData.vehicleId,
      vehicle_id: this.driverData.vehicleId,
      driverSno: this.driverData.driverSno, // Critical for admin to identify user
      status: this.driverData.status || "Active",
      oStatus: "F",
      latitude: "",
      longitude: "",
      timestamp: new Date().toISOString(),
    };

    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(payload));
        console.log("[LocationWS] Sent offline signal for:", this.driverData.vehicleId, "- oStatus:F with last location");
        // Give server a moment to process before closing
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (err) {
      console.error("[LocationWS] Failed to send offline signal:", err);
    }
  }

  /**
   * Update driver data (e.g., new location)
   */
  updateDriverData(driverData) {
    this.driverData = { ...this.driverData, ...driverData };
  }

  /**
   * Disconnect WebSocket and cleanup
   */
  disconnect() {
    this.manuallyDisconnected = true; // Prevent auto-reconnect
    
    // Cancel any pending reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
      console.log("[LocationWS] Cancelled pending reconnect");
    }
    
    if (this.sendLocationInterval) {
      clearInterval(this.sendLocationInterval);
      this.sendLocationInterval = null;
    }
    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN) {
          // Send offline signal and wait, then close
          this.sendOfflineSignal().then(() => {
            try {
              if (this.ws) this.ws.close();
            } catch (err) {
              console.error("[LocationWS] Error closing after offline signal:", err);
            }
          }).catch((err) => {
            console.error("[LocationWS] Offline signal failed, closing anyway:", err);
            try {
              if (this.ws) this.ws.close();
            } catch (e) {}
          });
        } else {
          // Already closed, just clean up
          try {
            if (this.ws) this.ws.close();
          } catch (err) {
            console.error("[LocationWS] Error closing (not open):", err);
          }
        }
      } catch (err) {
        console.error("[LocationWS] Error in disconnect:", err);
      }
      this.ws = null;
    }
    this.isConnected = false;
    this.reconnectAttempts = 0; // Reset reconnect counter
    console.log("[LocationWS] Disconnected - Auto-reconnect DISABLED");
  }

  /**
   * Attempt to reconnect with backoff
   */
  attemptReconnect() {
    // Don't reconnect if manually disconnected
    if (this.manuallyDisconnected) {
      console.log("[LocationWS] Manual disconnect in progress - skipping reconnect");
      return;
    }
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("[LocationWS] Max reconnect attempts reached");
      return;
    }
    this.reconnectAttempts += 1;
    const delay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts);
    console.log(`[LocationWS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    this.reconnectTimeout = setTimeout(() => {
      // Check again before reconnecting
      if (!this.manuallyDisconnected && this.authKey && this.driverData) {
        this.connect(this.authKey, this.driverData);
      }
    }, Math.min(delay, 30000));
  }

  /**
   * Check if connected
   */
  isReady() {
    return this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Export singleton
const locationWS = new LocationWebSocket();

// Global access for debugging
if (typeof window !== "undefined") {
  window.locationWS = locationWS;
}

export default locationWS;
